import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Avatar, Tag, Button, Spin, Select, Input, App } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { apiGetPatientDetails, apiUpdatePatient } from "../../api/patients";

const { TextArea } = Input;

const PatientDetails = () => {
  const { message } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [consultationNotes, setConsultationNotes] = useState("");
  const [selectedRiskLevel, setSelectedRiskLevel] = useState(1);
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const fetchPatientsData = async () => {
    try {
      setLoading(true);
      const res = await apiGetPatientDetails(id);
      setPatientData(res.data);
      setConsultationNotes(res.data?.doctorConsultationNotes || "");
      setSelectedRiskLevel(res.data?.riskLevel);
    } catch (error) {
      console.error("Patient fetch error:", error);
      message.error("Không thể tải dữ liệu bệnh nhân");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle save consultation notes
  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true);

      // Call the API to update consultation notes
      await apiUpdatePatient({
        patientId: parseInt(id),
        riskLevel: selectedRiskLevel ?? 1,
        doctorConsultationNotes: consultationNotes,
      });

      message.success("Ghi chú tư vấn đã được lưu thành công");
      setIsEditingNotes(false);

      // Update local state
      setPatientData({
        ...patientData,
        consultationNotes: consultationNotes,
      });
    } catch (error) {
      console.error("Error saving notes:", error);
      message.error("Không thể lưu ghi chú tư vấn");
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Handle risk level change
  const handleRiskLevelChange = async (value) => {
    try {
      setSelectedRiskLevel(value);

      // API call to update risk level
      await apiUpdatePatient({
        patientId: parseInt(id),
        riskLevel: value ?? 1,
        doctorConsultationNotes: consultationNotes,
      });

      message.success("Mức độ rủi ro đã được cập nhật thành công");

      // Update local state
      setPatientData({
        ...patientData,
        riskLevel: value,
      });
    } catch (error) {
      console.error("Error updating risk level:", error);
      message.error("Không thể cập nhật mức độ rủi ro");
      // Revert the selection on error
      setSelectedRiskLevel(patientData?.riskLevel);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!patientData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Không tìm thấy bệnh nhân
            </h2>
            <p className="text-gray-500 mb-6">
              Bệnh nhân có mã {id} không tồn tại.
            </p>
            <Button type="primary" onClick={() => navigate("/patients")}>
              Trở lại trang Bệnh nhân
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header with Back Button */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/patients")}
          className="mb-4"
          type="text"
        >
          Trở lại
        </Button>
        <h1 className="text-2xl font-semibold text-gray-800">
          Thông tin bệnh nhân
        </h1>
        <p className="text-sm text-gray-500 mt-1">Xem thông tin chi tiết bệnh nhân</p>
      </div>

      {/* Patient Header Card */}
      <Card className="shadow-sm mb-6 rounded-lg">
        <div className="flex items-start justify-between">
          {/* Left Section - Avatar and Info */}
          <div className="flex items-center gap-4">
            <Avatar
              size={80}
              src={patientData?.profileImage}
              style={{
                backgroundColor: patientData?.profileImage
                  ? undefined
                  : "#8B5CF6",
              }}
              className="text-2xl font-semibold"
            >
              {!patientData?.profileImage &&
                patientData?.fullName?.charAt(0)?.toUpperCase()}
            </Avatar>

            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {patientData?.fullName}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {patientData?.consultationType || "Weight Loss Consultation"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {patientData?.patientId || `Patient ${id}`}
              </p>
            </div>
          </div>

          {/* Right Section - Status and Risk Level */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Status:</span>
              <Tag
                color={patientData?.isActive ? "green" : "default"}
                className="rounded-full px-3 py-1"
              >
                {patientData?.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Risk Level:</span>
              <Select
                value={selectedRiskLevel}
                onChange={handleRiskLevelChange}
                className="w-32"
                options={[
                  { value: 1, label: "Low" },
                  { value: 2, label: "Medium" },
                  { value: 3, label: "High" },
                ]}
                style={{
                  borderRadius: "20px",
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Patient Record */}
        <Card title="Patient Record" className="shadow-sm rounded-lg">
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">Tổi:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.age || "N/A"} năm
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">Chiều cao:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.height || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">Trọng lượng hiện tại:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.currentWeight
                  ? `${patientData.currentWeight} lbs`
                  : "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">BMI:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.bmi || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-sm text-gray-600">Cân nặng mục tiêu:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.goalWeight
                  ? `${patientData.goalWeight} lbs`
                  : "N/A"}
              </span>
            </div>
          </div>
        </Card>

        {/* Current Medications */}
        <Card title="Các loại thuốc hiện đang sử dụng" className="shadow-sm rounded-lg">
          {patientData?.currentMedications &&
          patientData.currentMedications.length > 0 ? (
            <ul className="space-y-3">
              {patientData.currentMedications.map((med, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold mt-0.5">•</span>
                  <span className="text-sm text-gray-700">{med}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">Hiện không sử dụng thuốc nào</p>
          )}
        </Card>
      </div>

      {/* Consultation Notes and View Appointment Button */}
      <div className="flex flex-col gap-4">
        <Card
          title="Consultation Notes"
          extra={
            <Button
              icon={isEditingNotes ? <SaveOutlined /> : <EditOutlined />}
              color="primary"
              variant="link"
              className="text-purple-600 hover:text-purple-700"
              onClick={() => {
                if (isEditingNotes) {
                  handleSaveNotes();
                } else {
                  setIsEditingNotes(true);
                }
              }}
              loading={isSavingNotes}
              disabled={isSavingNotes}
            >
              {isEditingNotes ? "Lưu" : "Sửa"}
            </Button>
          }
          className="shadow-sm rounded-lg"
        >
          {isEditingNotes ? (
            <TextArea
              value={consultationNotes}
              onChange={(e) => setConsultationNotes(e.target.value)}
              placeholder="Nhập ghi chú tư vấn..."
              autoSize={{ minRows: 4, maxRows: 8 }}
              className="text-sm"
              disabled={isSavingNotes}
            />
          ) : (
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {consultationNotes ||
                "Hiện chưa có ghi chú tư vấn nào. Vui lòng nhấp vào Chỉnh sửa để thêm ghi chú."}
            </p>
          )}
        </Card>

        {/* View Appointment Button - Aligned to Right */}
        <div className="flex justify-end">
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            onClick={() =>
              navigate(`/appointments/${patientData?.upcomingAppointment}`)
            }
            size="large"
            disabled={!patientData?.upcomingAppointment}
          >
            Xem lịch hẹn
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PatientDetails;
